import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Cursor,
  PlusSquare,
  PencilSimple,
  PencilSimpleLine,
  HandGrabbing,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  ArrowCounterClockwise,
  ArrowClockwise,
  ArrowsClockwise,
  UploadSimple,
  Stack,
  Sparkle,
  X,
} from "@phosphor-icons/react";
import { fmtIn } from "./sketchGeom";
import { api, photoHref } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";

// ---- geometry helpers (absolute inches) -----------------------------------
const r2 = (n) => Math.round(n * 10000) / 10000;
const uid = () => Math.random().toString(36).slice(2, 10);
const STD_DEPTH = 25.5; // standard countertop depth applied on draw

const polyArea = (pts) =>
  Math.abs(
    pts.reduce((s, [x, y], i) => {
      const [x2, y2] = pts[(i + 1) % pts.length];
      return s + x * y2 - x2 * y;
    }, 0) / 2,
  );
const sqft = (pts) => polyArea(pts) / 144;
const edgeLen = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
};
const edgeMid = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
};
const insidePoly = ([x, y], pts) => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};
// unit outward normal of edge i
const outNormal = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const L = edgeLen(pts, i) || 1;
  let n = [(b[1] - a[1]) / L, -(b[0] - a[0]) / L];
  const m = edgeMid(pts, i);
  if (insidePoly([m[0] + n[0] * 0.5, m[1] + n[1] * 0.5], pts)) n = [-n[0], -n[1]];
  return n;
};
const snap = (v, step = 0.25) => r2(Math.round(v / step) * step);

// Segment intersection: returns {pt,t,u} with t along p1→p2, u along p3→p4, both in [0,1].
const segInt = (p1, p2, p3, p4) => {
  const rx = p2[0] - p1[0];
  const ry = p2[1] - p1[1];
  const sx = p4[0] - p3[0];
  const sy = p4[1] - p3[1];
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((p3[0] - p1[0]) * sy - (p3[1] - p1[1]) * sx) / den;
  const u = ((p3[0] - p1[0]) * ry - (p3[1] - p1[1]) * rx) / den;
  if (t < -1e-6 || t > 1 + 1e-6 || u < -1e-6 || u > 1 + 1e-6) return null;
  return { pt: [p1[0] + t * rx, p1[1] + t * ry], t, u };
};

// Split a simple polygon with an open seam polyline that cuts across it edge-to-edge.
// Supports a straight seam and a single-bend (corner) seam. Returns {poly1, poly2} or null.
const splitPolygonByPath = (poly, path) => {
  const n = poly.length;
  const cross = [];
  for (let si = 0; si < path.length - 1; si++) {
    for (let e = 0; e < n; e++) {
      const x = segInt(path[si], path[si + 1], poly[e], poly[(e + 1) % n]);
      if (x) cross.push({ pt: x.pt, edge: e, order: si + x.t });
    }
  }
  cross.sort((a, b) => a.order - b.order);
  const uniq = cross.filter(
    (c, i) => i === 0 || Math.hypot(c.pt[0] - cross[i - 1].pt[0], c.pt[1] - cross[i - 1].pt[1]) > 0.1,
  );
  if (uniq.length < 2) return null;
  const A = uniq[0];
  const B = uniq[uniq.length - 1];
  if (A.edge === B.edge) return null;
  const way = [];
  for (let k = 1; k < path.length - 1; k++) {
    if (k > A.order && k < B.order && insidePoly(path[k], poly)) way.push(path[k]);
  }
  const walk = (from, to) => {
    const out = [];
    let i = (from + 1) % n;
    for (let guard = 0; guard <= n; guard++) {
      out.push(poly[i]);
      if (i === to) break;
      i = (i + 1) % n;
    }
    return out;
  };
  const dedupe = (pts) =>
    pts.filter((p, i) => {
      const q = pts[(i + pts.length - 1) % pts.length];
      return Math.hypot(p[0] - q[0], p[1] - q[1]) > 0.05;
    });
  const poly1 = dedupe([A.pt, ...way, B.pt, ...walk(B.edge, A.edge)]);
  const poly2 = dedupe([B.pt, ...[...way].reverse(), A.pt, ...walk(A.edge, B.edge)]);
  if (poly1.length < 3 || poly2.length < 3) return null;
  return { poly1, poly2 };
};

// Slide the two corners after edge i so its length becomes L (keeps rect/L square).
const setSideLengthAbs = (pts, i, L) => {
  const n = pts.length;
  const a = pts[i];
  const b = pts[(i + 1) % n];
  const cur = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (cur < 0.01 || L <= 0.01) return pts;
  const ux = (b[0] - a[0]) / cur;
  const uy = (b[1] - a[1]) / cur;
  const d = L - cur;
  const out = pts.map((p) => [...p]);
  [(i + 1) % n, (i + 2) % n].forEach((k) => {
    out[k] = [r2(out[k][0] + ux * d), r2(out[k][1] + uy * d)];
  });
  return out;
};

// Move edge i along its outward normal by delta (deepen / resize a side).
const moveEdgeAbs = (pts, i, delta) => {
  const n = pts.length;
  const nrm = outNormal(pts, i);
  const out = pts.map((p) => [...p]);
  [i, (i + 1) % n].forEach((k) => {
    out[k] = [r2(out[k][0] + nrm[0] * delta), r2(out[k][1] + nrm[1] * delta)];
  });
  return out;
};

const lineInt = (p, d1, q, d2) => {
  const den = d1[0] * d2[1] - d1[1] * d2[0];
  if (Math.abs(den) < 1e-9) return null;
  const t = ((q[0] - p[0]) * d2[1] - (q[1] - p[1]) * d2[0]) / den;
  return [p[0] + d1[0] * t, p[1] + d1[1] * t];
};

// SVG path for a piece with optional rounded corners (p.r) and bowed sides (p.curve).
const piecePathD = (p, ts) => {
  const pts = p.pts;
  const n = pts.length;
  const R = p.r || {};
  const C = p.curve || {};
  const dir = pts.map((a, i) => {
    const b = pts[(i + 1) % n];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    return [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
  });
  const elen = (i) => Math.hypot(pts[(i + 1) % n][0] - pts[i][0], pts[(i + 1) % n][1] - pts[i][1]);
  const tr = (i) => Math.min(R[i] || 0, 0.49 * elen(i), 0.49 * elen((i - 1 + n) % n));
  const p1 = (i) => [pts[i][0] + dir[i][0] * tr(i), pts[i][1] + dir[i][1] * tr(i)];
  const p2 = (i) => [pts[(i + 1) % n][0] - dir[i][0] * tr((i + 1) % n), pts[(i + 1) % n][1] - dir[i][1] * tr((i + 1) % n)];
  const S = (w) => ts(w).join(",");
  let d = `M ${S(p1(0))}`;
  for (let i = 0; i < n; i++) {
    const a = p1(i);
    const b = p2(i);
    if (C[i]) {
      const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const nrm = outNormal(pts, i);
      d += ` Q ${S([mid[0] + nrm[0] * 2 * C[i], mid[1] + nrm[1] * 2 * C[i]])} ${S(b)}`;
    } else {
      d += ` L ${S(b)}`;
    }
    const j = (i + 1) % n;
    d += ` Q ${S(pts[j])} ${S(p1(j))}`;
  }
  return d + " Z";
};

// Insert a rectangular bump-out (dep>0) or notch (dep<0) into edge i, centred on the edge.
const insertBumpout = (pts, i, w, dep) => {
  const n = pts.length;
  const A = pts[i];
  const B = pts[(i + 1) % n];
  const L = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
  const d = [(B[0] - A[0]) / L, (B[1] - A[1]) / L];
  const nrm = outNormal(pts, i);
  const ww = Math.max(2, Math.min(w, L - 2));
  const s = L / 2 - ww / 2;
  const at = (t) => [A[0] + d[0] * t, A[1] + d[1] * t];
  const m0 = at(s);
  const m3 = at(s + ww);
  const m1 = [m0[0] + nrm[0] * dep, m0[1] + nrm[1] * dep];
  const m2 = [m3[0] + nrm[0] * dep, m3[1] + nrm[1] * dep];
  const out = pts.map((p) => [...p]);
  out.splice(i + 1, 0, m0, m1, m2, m3);
  return out;
};

// Replace a corner with a 45-degree chamfer of length c along both edges.
const chamferCorner = (pts, i, c) => {
  const n = pts.length;
  const prev = pts[(i - 1 + n) % n];
  const V = pts[i];
  const next = pts[(i + 1) % n];
  const Lp = Math.hypot(V[0] - prev[0], V[1] - prev[1]) || 1;
  const Ln = Math.hypot(next[0] - V[0], next[1] - V[1]) || 1;
  const cp = Math.min(c, 0.49 * Lp);
  const cn = Math.min(c, 0.49 * Ln);
  const inDir = [(V[0] - prev[0]) / Lp, (V[1] - prev[1]) / Lp];
  const outDir = [(next[0] - V[0]) / Ln, (next[1] - V[1]) / Ln];
  const out = pts.map((p) => [...p]);
  out.splice(
    i,
    1,
    [V[0] - inDir[0] * cp, V[1] - inDir[1] * cp],
    [V[0] + outDir[0] * cn, V[1] + outDir[1] * cn],
  );
  return out;
};

const CUT_PRESETS = [
  { kind: "Undermount sink", w: 30, h: 18, shape: "rect" },
  { kind: "Drop-in sink", w: 33, h: 22, shape: "rect" },
  { kind: "Farmhouse sink", w: 30, h: 20, shape: "rect" },
  { kind: "Cooktop", w: 30, h: 21, shape: "rect" },
  { kind: "Faucet hole", w: 2, h: 2, shape: "round" },
];

// ---- Step 6 slab-nesting helpers ------------------------------------------
const DEF_SLAB_L = 126; // default slab size (inches)
const DEF_SLAB_W = 63;
const NEST_GAP = 2; // inches between pieces when auto-nesting

const pieceBBox = (p) => {
  const xs = p.pts.map((v) => v[0]);
  const ys = p.pts.map((v) => v[1]);
  const minx = Math.min(...xs);
  const maxx = Math.max(...xs);
  const miny = Math.min(...ys);
  const maxy = Math.max(...ys);
  return { minx, miny, maxx, maxy, w: maxx - minx, h: maxy - miny, cx: (minx + maxx) / 2, cy: (miny + maxy) / 2 };
};

// Simple shelf packing → center placements, spilling onto new slabs as needed.
// Pieces are rotated 90° when that is the only way they fit; pieces too big for the
// slab in either orientation are returned in `oversize` (they need a seam / larger slab).
const packNest = (list, L, W) => {
  const place = {};
  let x = NEST_GAP;
  let y = NEST_GAP;
  let shelfH = 0;
  let slabIdx = 0;
  const oversize = [];
  const fits = (ww, hh) => ww + 2 * NEST_GAP <= L && hh + 2 * NEST_GAP <= W;
  const sorted = [...list].sort((a, b) => pieceBBox(b).h - pieceBBox(a).h);
  for (const p of sorted) {
    const bb = pieceBBox(p);
    let w = bb.w;
    let h = bb.h;
    let rot = 0;
    if (!fits(w, h) && fits(h, w)) {
      w = bb.h;
      h = bb.w;
      rot = 90;
    }
    if (!fits(w, h)) oversize.push(p.id);
    if (x + w + NEST_GAP > L) {
      x = NEST_GAP;
      y += shelfH + NEST_GAP;
      shelfH = 0;
    }
    if (y + h + NEST_GAP > W) {
      slabIdx += 1;
      x = NEST_GAP;
      y = NEST_GAP;
      shelfH = 0;
    }
    place[p.id] = { slabIdx, cx: x + w / 2, cy: y + h / 2, rot };
    x += w + NEST_GAP;
    shelfH = Math.max(shelfH, h);
  }
  return { place, count: slabIdx + 1, oversize };
};

// Build an orthogonal countertop ribbon of width `depth` following `path`.
const ribbonFromPath = (path, depth) => {
  if (path.length < 2) return null;
  const segs = [];
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    segs.push({ a, b, d: [(b[0] - a[0]) / L, (b[1] - a[1]) / L] });
  }
  // net turn to pick the concave (inner) offset side
  let turn = 0;
  for (let i = 0; i < segs.length - 1; i++) {
    turn += segs[i].d[0] * segs[i + 1].d[1] - segs[i].d[1] * segs[i + 1].d[0];
  }
  let sign = turn >= 0 ? 1 : -1;
  if (segs.length === 1) {
    // straight run: force the depth downward (or rightward for vertical runs)
    const ln = [-segs[0].d[1] * sign, segs[0].d[0] * sign];
    if (Math.abs(ln[1]) > 0.01) sign = ln[1] > 0 ? sign : -sign;
    else sign = ln[0] > 0 ? sign : -sign;
  }
  const off = segs.map((s) => [-s.d[1] * sign * depth, s.d[0] * sign * depth]);
  const offVerts = [];
  offVerts.push([segs[0].a[0] + off[0][0], segs[0].a[1] + off[0][1]]);
  for (let i = 1; i < segs.length; i++) {
    const p = [segs[i - 1].a[0] + off[i - 1][0], segs[i - 1].a[1] + off[i - 1][1]];
    const q = [segs[i].a[0] + off[i][0], segs[i].a[1] + off[i][1]];
    const x = lineInt(p, segs[i - 1].d, q, segs[i].d) || q;
    offVerts.push(x);
  }
  const last = segs[segs.length - 1];
  offVerts.push([last.b[0] + off[off.length - 1][0], last.b[1] + off[off.length - 1][1]]);
  const poly = [...path, ...offVerts.reverse()].map(([x, y]) => [r2(x), r2(y)]);
  return poly;
};

const TOOLS = [
  ["select", Cursor, "Select", "V"],
  ["create", PlusSquare, "Create", "N"],
  ["edit", PencilSimple, "Edit", "E"],
  ["draw", PencilSimpleLine, "Draw", "D"],
  ["pan", HandGrabbing, "Pan", "H"],
];

const STEPS = [
  "Counter Dimensions",
  "Curves & Radices",
  "Counter Bumpouts",
  "Edge & Backsplash",
  "Side & Countertop",
  "Colors & Layout",
  "Project Details",
];

export const StoneExDrawing = ({ onExit, onSendToQuote, areaId = "", fallbackRate = 0 }) => {
  const svgRef = useRef(null);
  const [tool, setTool] = useState("draw");
  const [pieces, setPieces] = useState([]);
  const [sel, setSel] = useState(null);
  const [view, setView] = useState({ scale: 3, ox: 60, oy: 60 }); // px per inch + offset
  const [thickness, setThickness] = useState("3cm");
  const [edgeProfile] = useState("Eased Polish");
  const [dimEdit, setDimEdit] = useState(null); // {pieceId, edge, value, sx, sy}
  const [gesture, setGesture] = useState(null); // active pointer gesture
  const [draw, setDraw] = useState(null); // {path, live, axis}
  const [step, setStep] = useState(1);
  const [cornerPopup, setCornerPopup] = useState(null);
  const [edgeSel, setEdgeSel] = useState(null); // {pieceId, i}
  const [bumpW, setBumpW] = useState(12);
  const [bumpD, setBumpD] = useState(6);
  const [cutSel, setCutSel] = useState(null); // {pieceId, id}
  const past = useRef([]);
  const future = useRef([]);

  // ---- Step 6 (Colors & Layout / slab nesting) ----------------------------
  const [slabLib, setSlabLib] = useState([]);
  const [boards, setBoards] = useState([
    { id: uid(), label: "Slab 1", L: DEF_SLAB_L, W: DEF_SLAB_W, photo_url: "", slab_id: "", rate: 0 },
  ]);
  const [activeBoard, setActiveBoard] = useState(0);
  const [nest, setNest] = useState({});
  const [nestSel, setNestSel] = useState(null);
  const [oversize, setOversize] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [billMethod, setBillMethod] = useState("sf"); // sf = per-SF real waste, slab = by slabs
  const nestPhotoRef = useRef(null);
  const [seamMenu, setSeamMenu] = useState(null); // {pieceId, sx, sy}
  const [seamDraw, setSeamDraw] = useState(null); // {pieceId, path, live, axis}
  const seamOrig = useRef({}); // group id -> { piece, place } for Delete Seam
  const lastTap = useRef({ pid: null, t: 0 }); // double-tap detection for the seam menu

  const toScreen = ([x, y]) => [x * view.scale + view.ox, y * view.scale + view.oy];
  const toWorld = (sx, sy) => {
    const r = svgRef.current.getBoundingClientRect();
    return [(sx - r.left - view.ox) / view.scale, (sy - r.top - view.oy) / view.scale];
  };

  const commit = useCallback((next) => {
    setPieces((cur) => {
      past.current.push(cur);
      future.current = [];
      return typeof next === "function" ? next(cur) : next;
    });
  }, []);
  const undo = () => {
    if (!past.current.length) return;
    setPieces((cur) => {
      future.current.push(cur);
      return past.current.pop();
    });
  };
  const redo = () => {
    if (!future.current.length) return;
    setPieces((cur) => {
      past.current.push(cur);
      return future.current.pop();
    });
  };

  const totalArea = useMemo(() => pieces.reduce((s, p) => s + sqft(p.pts), 0), [pieces]);

  // ---- Step 6 slab boards, nesting & quote estimate ------------------------
  const boardOrigins = useMemo(() => {
    let y = 0;
    return boards.map((b) => {
      const o = { x: 0, y };
      y += (b.W || DEF_SLAB_W) + 24;
      return o;
    });
  }, [boards]);

  const placePoly = (p, pl, origin) => {
    const bb = pieceBBox(p);
    const rad = ((pl.rot || 0) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return p.pts.map(([x, y]) => {
      const lx = x - bb.cx;
      const ly = y - bb.cy;
      return [origin.x + pl.cx + (lx * cos - ly * sin), origin.y + pl.cy + (lx * sin + ly * cos)];
    });
  };

  // true when a placed piece fits entirely within its slab board
  const fitsBoard = (p, pl) => {
    const b = boards[pl.board];
    const o = boardOrigins[pl.board];
    if (!b || !o) return true;
    const poly = placePoly(p, pl, o);
    return poly.every(
      ([x, y]) => x >= o.x - 0.1 && x <= o.x + b.L + 0.1 && y >= o.y - 0.1 && y <= o.y + b.W + 0.1,
    );
  };

  const boardUsage = useMemo(
    () =>
      boards.map((b, i) => {
        const used = Object.entries(nest).reduce((s, [pid, pl]) => {
          if (pl.board !== i) return s;
          const p = pieces.find((x) => x.id === pid);
          return p ? s + sqft(p.pts) : s;
        }, 0);
        const slabSF = (b.L * b.W) / 144;
        return { used, slabSF, offcut: Math.max(0, slabSF - used) };
      }),
    [boards, nest, pieces],
  );

  const nestTotals = useMemo(() => {
    const usedIdx = boards.map((_, i) => i).filter((i) => Object.values(nest).some((pl) => pl.board === i));
    const finished = Object.keys(nest).reduce((s, pid) => {
      const p = pieces.find((x) => x.id === pid);
      return p ? s + sqft(p.pts) : s;
    }, 0);
    const consumed = usedIdx.reduce((s, i) => s + boardUsage[i].slabSF, 0);
    const rate = boards[activeBoard]?.rate || boards.find((b) => b.rate > 0)?.rate || fallbackRate || 0;
    return { finished, consumed, waste: Math.max(0, consumed - finished), slabs: usedIdx.length, rate };
  }, [boards, nest, pieces, boardUsage, activeBoard, fallbackRate]);

  useEffect(() => {
    api.get("/slabs").then((r) => setSlabLib(r.data || [])).catch(() => {});
  }, []);

  const runAutoNest = () => {
    if (!pieces.length) {
      toast("Draw a counter first");
      return;
    }
    const b0 = boards[0] || { L: DEF_SLAB_L, W: DEF_SLAB_W };
    const { place, count, oversize: over } = packNest(pieces, b0.L, b0.W);
    setBoards((bs) => {
      const next = [...bs];
      while (next.length < count) {
        const src = next[0] || {};
        next.push({
          id: uid(),
          label: `Slab ${next.length + 1}`,
          L: src.L || DEF_SLAB_L,
          W: src.W || DEF_SLAB_W,
          photo_url: src.photo_url || "",
          slab_id: src.slab_id || "",
          rate: src.rate || 0,
        });
      }
      return next;
    });
    const nx = {};
    Object.entries(place).forEach(([pid, pl]) => {
      nx[pid] = { board: pl.slabIdx, cx: pl.cx, cy: pl.cy, rot: 0 };
    });
    setNest(nx);
    setOversize(over);
    if (over.length) {
      toast.error(`${over.length} piece(s) are larger than the slab — add a seam or a bigger slab`);
    } else {
      toast.success(`Nested ${pieces.length} piece(s) onto ${count} slab(s)`);
    }
  };

  // auto-place once when first entering Step 6 with nothing placed yet
  useEffect(() => {
    if (step === 6 && pieces.length && Object.keys(nest).length === 0) runAutoNest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const nestDown = (wx, wy) => {
    for (const pid of Object.keys(nest)) {
      const p = pieces.find((x) => x.id === pid);
      const pl = nest[pid];
      const origin = boardOrigins[pl.board];
      if (!p || !origin) continue;
      if (insidePoly([wx, wy], placePoly(p, pl, origin))) {
        const now = Date.now();
        if (lastTap.current.pid === pid && now - lastTap.current.t < 400) {
          const s = toScreen([wx, wy]);
          setNestSel(pid);
          setActiveBoard(pl.board);
          setSeamMenu({ pieceId: pid, sx: s[0], sy: s[1] });
          lastTap.current = { pid: null, t: 0 };
          return;
        }
        lastTap.current = { pid, t: now };
        setNestSel(pid);
        setActiveBoard(pl.board);
        setGesture({ type: "nestMove", pieceId: pid, start: [wx, wy], orig: { cx: pl.cx, cy: pl.cy } });
        return;
      }
    }
    for (let i = 0; i < boards.length; i++) {
      const o = boardOrigins[i];
      const b = boards[i];
      if (wx >= o.x && wx <= o.x + b.L && wy >= o.y && wy <= o.y + b.W) {
        setActiveBoard(i);
        setNestSel(null);
        return;
      }
    }
    setNestSel(null);
  };

  const rotateNestSel = () => {
    if (!nestSel) return;
    setNest((n) => (n[nestSel] ? { ...n, [nestSel]: { ...n[nestSel], rot: ((n[nestSel].rot || 0) + 90) % 360 } } : n));
  };

  const setBoard = (i, patch) => setBoards((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const pickInventorySlab = (id) => {
    const rec = slabLib.find((s) => s.id === id);
    if (!rec) {
      setBoard(activeBoard, { slab_id: "" });
      return;
    }
    const photo = (rec.photos || []).find((p) => !p.is_deleted);
    setBoard(activeBoard, {
      slab_id: id,
      label: rec.slab_color || rec.code || "Slab",
      L: Number(rec.length_in) || boards[activeBoard].L,
      W: Number(rec.width_in) || boards[activeBoard].W,
      rate: Number(rec.sale_price_per_sqft) || 0,
      ...(photo ? { photo_url: photoHref(photo.storage_path) } : {}),
    });
  };

  const addBoard = () => {
    setBoards((bs) => [
      ...bs,
      { id: uid(), label: `Slab ${bs.length + 1}`, L: DEF_SLAB_L, W: DEF_SLAB_W, photo_url: "", slab_id: "", rate: 0 },
    ]);
    setActiveBoard(boards.length);
  };

  const removeBoard = () => {
    if (boards.length <= 1) return;
    const idx = activeBoard;
    setBoards((bs) => bs.filter((_, j) => j !== idx));
    setNest((n) => {
      const nx = {};
      Object.entries(n).forEach(([pid, pl]) => {
        if (pl.board === idx) return;
        nx[pid] = { ...pl, board: pl.board > idx ? pl.board - 1 : pl.board };
      });
      return nx;
    });
    setActiveBoard((a) => Math.max(0, a - 1));
  };

  const uploadNestPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", await shrinkForUpload(file));
    try {
      const { data } = await api.post("/quotes/layout-photo", fd);
      setBoard(activeBoard, { photo_url: photoHref(data.storage_path) });
      toast.success("Slab photo uploaded");
    } catch {
      toast.error("Could not upload that photo");
    }
    setUploading(false);
  };

  const sendToQuote = () => {
    if (!onSendToQuote) return;
    const rate = nestTotals.rate;
    if (!rate) {
      toast("Pick an inventory slab to price material first");
      return;
    }
    const name = boards[activeBoard]?.label || boards.find((b) => b.slab_id)?.label || "Stone material";
    let line;
    if (billMethod === "slab") {
      const slabs = Math.max(1, nestTotals.slabs);
      const perSlab = Math.round((nestTotals.consumed * rate) / slabs);
      line = {
        area_id: areaId || "",
        kind: "material",
        is_material: true,
        item: name,
        description: `${name} · ${slabs} slab(s) · ${nestTotals.finished.toFixed(1)} SF finished · ${nestTotals.waste.toFixed(1)} SF waste`,
        qty: slabs,
        unit: "Slab",
        unit_price: perSlab,
        taxable: true,
        material_override: true,
      };
    } else {
      line = {
        area_id: areaId || "",
        kind: "material",
        is_material: true,
        item: name,
        description: `${name} · ${nestTotals.consumed.toFixed(1)} SF (incl. ${nestTotals.waste.toFixed(1)} SF real waste)`,
        qty: Number(nestTotals.consumed.toFixed(2)),
        unit: "SF",
        unit_price: Number(rate.toFixed(2)),
        taxable: true,
        material_override: true,
      };
    }
    onSendToQuote(line);
  };

  // ---- seams (Step 6: split an oversized piece to fit the slab) ------------
  const startSeam = (pieceId) => {
    setNestSel(pieceId);
    setSeamMenu(null);
    setSeamDraw({ pieceId, path: [], live: null, axis: null });
    toast("Drag across the piece where the seam goes");
  };

  const applySeam = (pieceId, path) => {
    const p = pieces.find((x) => x.id === pieceId);
    const pl = nest[pieceId];
    if (!p || !pl) return;
    const origin = boardOrigins[pl.board];
    const world = placePoly(p, pl, origin);
    const res = splitPolygonByPath(world, path);
    if (!res) {
      toast.error("Draw the seam across the piece, edge to edge");
      return;
    }
    if (sqft(res.poly1) < 0.5 || sqft(res.poly2) < 0.5) {
      toast.error("Seam is too close to the edge");
      return;
    }
    const gid = p.seam?.group || uid();
    if (!p.seam) seamOrig.current[gid] = { piece: p, place: pl };
    const mk = (pw) => {
      const pts = pw.map(([x, y]) => [r2(x), r2(y)]);
      const bb = pieceBBox({ pts });
      return {
        piece: { id: uid(), pts, seam: { group: gid } },
        place: { board: pl.board, cx: r2(bb.cx - origin.x), cy: r2(bb.cy - origin.y), rot: 0 },
      };
    };
    const c1 = mk(res.poly1);
    const c2 = mk(res.poly2);
    commit((cur) => [...cur.filter((x) => x.id !== pieceId), c1.piece, c2.piece]);
    setNest((nmap) => {
      const nx = { ...nmap };
      delete nx[pieceId];
      nx[c1.piece.id] = c1.place;
      nx[c2.piece.id] = c2.place;
      return nx;
    });
    setNestSel(c1.piece.id);
    // recompute off-board warning: drop the original piece, flag any child that hangs off the slab
    const childOver = [];
    if (!fitsBoard(c1.piece, c1.place)) childOver.push(c1.piece.id);
    if (!fitsBoard(c2.piece, c2.place)) childOver.push(c2.piece.id);
    setOversize((prev) => [...prev.filter((id) => id !== pieceId), ...childOver]);
    toast.success("Seam added — drag the two pieces apart");
  };

  const deleteSeam = (pieceId) => {
    const p = pieces.find((x) => x.id === pieceId);
    setSeamMenu(null);
    if (!p?.seam) {
      toast("This piece has no seam to remove");
      return;
    }
    const gid = p.seam.group;
    const orig = seamOrig.current[gid];
    if (!orig) {
      toast.error("Original piece unavailable");
      return;
    }
    const childIds = pieces.filter((x) => x.seam?.group === gid).map((x) => x.id);
    commit((cur) => [...cur.filter((x) => x.seam?.group !== gid), orig.piece]);
    setNest((nmap) => {
      const nx = { ...nmap };
      childIds.forEach((id) => delete nx[id]);
      nx[orig.piece.id] = orig.place;
      return nx;
    });
    setNestSel(orig.piece.id);
    toast.success("Seam removed");
  };

  // ---- pointer gestures ----------------------------------------------------
  const onDown = (e) => {
    if (e.button === 1) return;
    const [wx, wy] = toWorld(e.clientX, e.clientY);
    if (tool === "pan") {
      setGesture({ type: "pan", sx: e.clientX, sy: e.clientY, ox: view.ox, oy: view.oy });
      return;
    }
    if (step === 6) {
      setSeamMenu(null);
      if (seamDraw) {
        const sp = [wx, wy];
        setSeamDraw((d) => (d ? { ...d, path: [sp], live: sp, axis: null } : d));
        setGesture({ type: "seam" });
        return;
      }
      nestDown(wx, wy);
      return;
    }
    if (tool === "draw") {
      const p = [snap(wx), snap(wy)];
      setDraw({ path: [p], live: p, axis: null });
      setGesture({ type: "draw" });
      return;
    }
    // select / edit: hit test edges then bodies
    const hitEdge = edgeHit(wx, wy);
    if (hitEdge) {
      setSel(hitEdge.pieceId);
      if (step === 2 || step === 3 || step === 4) {
        setEdgeSel({ pieceId: hitEdge.pieceId, i: hitEdge.edge });
        return;
      }
      setGesture({
        type: "edge",
        pieceId: hitEdge.pieceId,
        edge: hitEdge.edge,
        orig: pieces.find((p) => p.id === hitEdge.pieceId).pts,
        start: [wx, wy],
      });
      return;
    }
    const body = [...pieces].reverse().find((p) => insidePoly([wx, wy], p.pts));
    if (body) {
      setSel(body.id);
      setGesture({ type: "move", pieceId: body.id, orig: body.pts, start: [wx, wy] });
    } else {
      setSel(null);
    }
  };

  const onMove = (e) => {
    if (!gesture) return;
    if (gesture.type === "pan") {
      setView((v) => ({ ...v, ox: gesture.ox + (e.clientX - gesture.sx), oy: gesture.oy + (e.clientY - gesture.sy) }));
      return;
    }
    const [wx, wy] = toWorld(e.clientX, e.clientY);
    if (gesture.type === "nestMove") {
      const dx = wx - gesture.start[0];
      const dy = wy - gesture.start[1];
      setNest((n) => {
        const cur = n[gesture.pieceId];
        if (!cur) return n;
        return { ...n, [gesture.pieceId]: { ...cur, cx: r2(gesture.orig.cx + dx), cy: r2(gesture.orig.cy + dy) } };
      });
      return;
    }
    if (gesture.type === "seam") {
      setSeamDraw((d) => {
        if (!d) return d;
        const lastV = d.path[d.path.length - 1];
        const dx = wx - lastV[0];
        const dy = wy - lastV[1];
        const BEND = 8;
        if (!d.axis) {
          const axis = Math.abs(dx) >= Math.abs(dy) ? "h" : "v";
          return { ...d, live: axis === "h" ? [wx, lastV[1]] : [lastV[0], wy], axis };
        }
        if (d.axis === "h") {
          if (Math.abs(dy) > BEND) {
            const corner = [wx, lastV[1]];
            return { ...d, path: [...d.path, corner], live: [corner[0], wy], axis: "v" };
          }
          return { ...d, live: [wx, lastV[1]] };
        }
        if (Math.abs(dx) > BEND) {
          const corner = [lastV[0], wy];
          return { ...d, path: [...d.path, corner], live: [wx, corner[1]], axis: "h" };
        }
        return { ...d, live: [lastV[0], wy] };
      });
      return;
    }
    if (gesture.type === "draw") {
      setDraw((d) => {
        if (!d) return d;
        const lastV = d.path[d.path.length - 1];
        const dx = wx - lastV[0];
        const dy = wy - lastV[1];
        const BEND = 8; // inches of perpendicular travel to start a new leg
        if (!d.axis) {
          const axis = Math.abs(dx) >= Math.abs(dy) ? "h" : "v";
          const live = axis === "h" ? [snap(wx), lastV[1]] : [lastV[0], snap(wy)];
          return { ...d, live, axis };
        }
        if (d.axis === "h") {
          if (Math.abs(dy) > BEND) {
            const corner = [snap(wx), lastV[1]];
            return { path: [...d.path, corner], live: [corner[0], snap(wy)], axis: "v" };
          }
          return { ...d, live: [snap(wx), lastV[1]] };
        }
        // current leg is vertical
        if (Math.abs(dx) > BEND) {
          const corner = [lastV[0], snap(wy)];
          return { path: [...d.path, corner], live: [snap(wx), corner[1]], axis: "h" };
        }
        return { ...d, live: [lastV[0], snap(wy)] };
      });
      return;
    }
    if (gesture.type === "move") {
      const dx = snap(wx - gesture.start[0]);
      const dy = snap(wy - gesture.start[1]);
      setPieces((cur) =>
        cur.map((p) =>
          p.id === gesture.pieceId
            ? { ...p, pts: gesture.orig.map(([x, y]) => [r2(x + dx), r2(y + dy)]) }
            : p,
        ),
      );
      return;
    }
    if (gesture.type === "cutout") {
      setPieces((cur) =>
        cur.map((p) =>
          p.id === gesture.pieceId
            ? {
                ...p,
                cutouts: (p.cutouts || []).map((c) =>
                  c.id === gesture.cutId ? { ...c, cx: snap(wx), cy: snap(wy) } : c,
                ),
              }
            : p,
        ),
      );
      return;
    }
    if (gesture.type === "curve") {
      const pc = pieces.find((p) => p.id === gesture.pieceId);
      if (pc) {
        const nrm = outNormal(pc.pts, gesture.edge);
        const depth = Math.max(0, snap((wx - gesture.mid[0]) * nrm[0] + (wy - gesture.mid[1]) * nrm[1]));
        setPieces((cur) =>
          cur.map((p) =>
            p.id === gesture.pieceId ? { ...p, curve: { ...(p.curve || {}), [gesture.edge]: depth } } : p,
          ),
        );
      }
      return;
    }
    if (gesture.type === "edge") {
      const nrm = outNormal(gesture.orig, gesture.edge);
      const delta = snap((wx - gesture.start[0]) * nrm[0] + (wy - gesture.start[1]) * nrm[1]);
      setPieces((cur) =>
        cur.map((p) =>
          p.id === gesture.pieceId ? { ...p, pts: moveEdgeAbs(gesture.orig, gesture.edge, delta) } : p,
        ),
      );
    }
  };

  const onUp = () => {
    if (gesture?.type === "seam") {
      if (seamDraw?.live) applySeam(seamDraw.pieceId, [...seamDraw.path, seamDraw.live]);
      setSeamDraw(null);
      setGesture(null);
      return;
    }
    if (gesture?.type === "nestMove") {
      const pieceId = gesture.pieceId;
      const cur = nest[pieceId];
      if (cur) {
        const oldO = boardOrigins[cur.board];
        if (oldO) {
          const wcx = oldO.x + cur.cx; // piece centre in world coords
          const wcy = oldO.y + cur.cy;
          let tgt = cur.board;
          for (let i = 0; i < boards.length; i++) {
            const o = boardOrigins[i];
            const b = boards[i];
            if (wcx >= o.x && wcx <= o.x + b.L && wcy >= o.y && wcy <= o.y + b.W) {
              tgt = i;
              break;
            }
          }
          if (tgt !== cur.board) {
            const nO = boardOrigins[tgt];
            const moved = { ...cur, board: tgt, cx: r2(wcx - nO.x), cy: r2(wcy - nO.y) };
            setNest((n) => ({ ...n, [pieceId]: moved }));
            setActiveBoard(tgt);
            const p = pieces.find((x) => x.id === pieceId);
            if (p) {
              const ok = fitsBoard(p, moved);
              setOversize((prev) =>
                ok ? prev.filter((id) => id !== pieceId) : prev.includes(pieceId) ? prev : [...prev, pieceId],
              );
            }
          }
        }
      }
      setGesture(null);
      return;
    }
    if (gesture?.type === "draw" && draw) {
      const path = [...draw.path, draw.live].filter(
        (p, i, a) => i === 0 || p[0] !== a[i - 1][0] || p[1] !== a[i - 1][1],
      );
      const poly = ribbonFromPath(path, STD_DEPTH);
      if (poly && sqft(poly) > 0.5) {
        const id = uid();
        commit((cur) => [...cur, { id, pts: poly }]);
        setSel(id);
        toast.success("Counter created — click a dimension to set its exact length");
      }
      setDraw(null);
    } else if (
      gesture?.type === "move" ||
      gesture?.type === "edge" ||
      gesture?.type === "curve" ||
      gesture?.type === "cutout"
    ) {
      // snapshot for undo on the finished drag
      past.current.push(gesture.orig ? pieces.map((p) => (p.id === gesture.pieceId ? { ...p, pts: gesture.orig } : p)) : pieces);
      future.current = [];
    }
    setGesture(null);
  };

  const edgeHit = (wx, wy) => {
    for (let pi = pieces.length - 1; pi >= 0; pi--) {
      const p = pieces[pi];
      for (let i = 0; i < p.pts.length; i++) {
        const a = p.pts[i];
        const b = p.pts[(i + 1) % p.pts.length];
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const L2 = dx * dx + dy * dy || 1;
        const t = Math.max(0, Math.min(1, ((wx - a[0]) * dx + (wy - a[1]) * dy) / L2));
        const dist = Math.hypot(wx - (a[0] + t * dx), wy - (a[1] + t * dy));
        if (dist < 4) return { pieceId: p.id, edge: i };
      }
    }
    return null;
  };

  const addRect = () => {
    const [wx, wy] = [40, 40];
    const id = uid();
    const pts = [
      [wx, wy],
      [wx + 60, wy],
      [wx + 60, wy + STD_DEPTH],
      [wx, wy + STD_DEPTH],
    ];
    commit((cur) => [...cur, { id, pts }]);
    setSel(id);
  };

  const applyDim = () => {
    if (!dimEdit) return;
    const L = parseFloat(dimEdit.value);
    if (L > 0) {
      commit((cur) =>
        cur.map((p) => (p.id === dimEdit.pieceId ? { ...p, pts: setSideLengthAbs(p.pts, dimEdit.edge, L) } : p)),
      );
    }
    setDimEdit(null);
  };

  const addCutoutShape = (preset) => {
    const target = sel ? pieces.find((p) => p.id === sel) : pieces[0];
    if (!target) {
      toast("Draw a counter first");
      return;
    }
    const cx = target.pts.reduce((s, p) => s + p[0], 0) / target.pts.length;
    const cy = target.pts.reduce((s, p) => s + p[1], 0) / target.pts.length;
    const id = `c${Date.now()}`;
    commit((cur) =>
      cur.map((p) =>
        p.id === target.id
          ? {
              ...p,
              cutouts: [
                ...(p.cutouts || []),
                { id, kind: preset.kind, cx, cy, w: preset.w, h: preset.h, shape: preset.shape },
              ],
            }
          : p,
      ),
    );
    setSel(target.id);
    setCutSel({ pieceId: target.id, id });
  };

  const removeCutout = () => {
    if (!cutSel) return;
    commit((cur) =>
      cur.map((p) =>
        p.id === cutSel.pieceId
          ? { ...p, cutouts: (p.cutouts || []).filter((c) => c.id !== cutSel.id) }
          : p,
      ),
    );
    setCutSel(null);
  };

  const applyRadius = () => {
    if (!cornerPopup) return;
    const val = parseFloat(cornerPopup.value) || 0;
    if (cornerPopup.mode === "chamfer") {
      commit((cur) =>
        cur.map((p) =>
          p.id === cornerPopup.pieceId
            ? { ...p, pts: chamferCorner(p.pts, cornerPopup.idx, val) }
            : p,
        ),
      );
    } else {
      commit((cur) =>
        cur.map((p) =>
          p.id === cornerPopup.pieceId
            ? { ...p, r: { ...(p.r || {}), [cornerPopup.idx]: val > 0 ? val : 0 } }
            : p,
        ),
      );
    }
    setCornerPopup(null);
  };

  const addBumpout = (sign) => {
    if (!edgeSel) return;
    const dep = sign * Math.max(1, Math.abs(bumpD));
    commit((cur) =>
      cur.map((p) =>
        p.id === edgeSel.pieceId ? { ...p, pts: insertBumpout(p.pts, edgeSel.i, bumpW, dep) } : p,
      ),
    );
    setEdgeSel(null);
  };

  const setEdgeMeta = (patch) => {
    if (!edgeSel) return;
    commit((cur) =>
      cur.map((p) =>
        p.id === edgeSel.pieceId
          ? {
              ...p,
              edgeMeta: {
                ...(p.edgeMeta || {}),
                [edgeSel.i]: { ...((p.edgeMeta || {})[edgeSel.i] || {}), ...patch },
              },
            }
          : p,
      ),
    );
  };

  const setCurveDepth = (v) => {
    if (!edgeSel) return;
    const dep = Math.max(0, parseFloat(v) || 0);
    commit((cur) =>
      cur.map((p) =>
        p.id === edgeSel.pieceId ? { ...p, curve: { ...(p.curve || {}), [edgeSel.i]: dep } } : p,
      ),
    );
  };

  const flatten = () => {
    if (!edgeSel) return;
    commit((cur) =>
      cur.map((p) => {
        if (p.id !== edgeSel.pieceId) return p;
        const c = { ...(p.curve || {}) };
        delete c[edgeSel.i];
        return { ...p, curve: c };
      }),
    );
  };

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
        return;
      }
      const t = TOOLS.find((x) => x[3].toLowerCase() === e.key.toLowerCase());
      if (t) setTool(t[0]);
      if (e.key === "Escape") {
        if (draw) setDraw(null);
        else setSel(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draw]);

  // ---- render --------------------------------------------------------------
  const drawPreview = draw ? ribbonFromPath([...draw.path, draw.live], STD_DEPTH) : null;

  return (
    <div
      data-testid="stoneex-drawing"
      className="flex h-full flex-col overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 text-zinc-200"
    >
      {/* header + step tabs */}
      <div className="shrink-0 border-b border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-900/60">
        <div className="flex items-center justify-between gap-3 px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-bold tracking-wide text-amber-300">STONEEX Drawing Tool</span>
            <span className="text-zinc-500">· Untitled countertop</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="stoneex-new"
              onClick={() => {
                commit([]);
                setSel(null);
              }}
              className="rounded-full border border-zinc-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-zinc-500"
            >
              New design
            </button>
            <button
              data-testid="stoneex-exit"
              onClick={onExit}
              className="rounded-full border border-zinc-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:border-zinc-500"
            >
              Exit
            </button>
          </div>
        </div>
        <div className="flex gap-1 overflow-x-auto px-3 pb-2">
          {STEPS.map((label, i) => (
            <button
              key={label}
              data-testid={`stoneex-step-${i + 1}`}
              onClick={() => (i <= 5 ? setStep(i + 1) : toast("Project Details (Step 7) arrives in the next phase"))}
              className={`shrink-0 rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors duration-150 ${
                step === i + 1
                  ? "bg-amber-400/15 text-amber-200"
                  : "text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-300"
              }`}
            >
              {i + 1}. {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* left toolbar */}
        <div className="flex w-14 shrink-0 flex-col items-center gap-1 border-r border-zinc-800 bg-zinc-900/60 py-3">
          {TOOLS.map(([key, Icon, label]) => (
            <button
              key={key}
              data-testid={`stoneex-tool-${key}`}
              title={label}
              onClick={() => (key === "create" ? addRect() : setTool(key))}
              className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors duration-150 ${
                tool === key
                  ? "bg-amber-400/20 text-amber-200 ring-1 ring-amber-400/40"
                  : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              }`}
            >
              <Icon size={20} weight="bold" />
            </button>
          ))}
          <div className="my-1 h-px w-8 bg-zinc-800" />
          <button
            data-testid="stoneex-zoom-in"
            title="Zoom in"
            onClick={() => setView((v) => ({ ...v, scale: Math.min(12, v.scale * 1.2) }))}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
          >
            <MagnifyingGlassPlus size={20} weight="bold" />
          </button>
          <button
            data-testid="stoneex-zoom-out"
            title="Zoom out"
            onClick={() => setView((v) => ({ ...v, scale: Math.max(1, v.scale / 1.2) }))}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
          >
            <MagnifyingGlassMinus size={20} weight="bold" />
          </button>
          <button
            data-testid="stoneex-undo"
            title="Undo"
            onClick={undo}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
          >
            <ArrowCounterClockwise size={20} weight="bold" />
          </button>
          <button
            data-testid="stoneex-redo"
            title="Redo"
            onClick={redo}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
          >
            <ArrowClockwise size={20} weight="bold" />
          </button>
        </div>

        {/* canvas */}
        <div className="relative min-w-0 flex-1 bg-zinc-950">
          <svg
            ref={svgRef}
            data-testid="stoneex-canvas"
            className={`h-full w-full ${seamDraw ? "cursor-crosshair" : tool === "pan" ? "cursor-grab" : tool === "draw" ? "cursor-crosshair" : "cursor-default"}`}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              onDown(e);
            }}
            onPointerMove={onMove}
            onPointerUp={onUp}
          >
            {/* grid */}
            <defs>
              <pattern id="sx-grid" width={12 * view.scale} height={12 * view.scale} patternUnits="userSpaceOnUse" x={view.ox} y={view.oy}>
                <path d={`M ${12 * view.scale} 0 L 0 0 0 ${12 * view.scale}`} fill="none" stroke="#27272a" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#sx-grid)" />

            {step === 6 &&
              boards.map((b, i) => {
                const o = boardOrigins[i];
                const tl = toScreen([o.x, o.y]);
                const br = toScreen([o.x + b.L, o.y + b.W]);
                const w = br[0] - tl[0];
                const h = br[1] - tl[1];
                const isAct = i === activeBoard;
                const clipId = `sx-slab-clip-${b.id}`;
                return (
                  <g key={b.id} data-testid={`stoneex-slab-${i}`}>
                    <defs>
                      <clipPath id={clipId}>
                        <rect x={tl[0]} y={tl[1]} width={w} height={h} rx={4} />
                      </clipPath>
                    </defs>
                    <rect
                      x={tl[0]}
                      y={tl[1]}
                      width={w}
                      height={h}
                      rx={4}
                      fill={b.photo_url ? "#18181b" : "#1f2937"}
                      stroke={isAct ? "#fbbf24" : "#52525b"}
                      strokeWidth={isAct ? 2.5 : 1.5}
                    />
                    {b.photo_url && (
                      <image
                        href={b.photo_url}
                        x={tl[0]}
                        y={tl[1]}
                        width={w}
                        height={h}
                        preserveAspectRatio="xMidYMid slice"
                        clipPath={`url(#${clipId})`}
                        opacity={0.9}
                      />
                    )}
                    <text x={tl[0] + 6} y={tl[1] + 16} fontSize="11" fontWeight="700" fill="#fcd34d">
                      {b.label} · {Math.round(b.L)}" × {Math.round(b.W)}"
                    </text>
                  </g>
                );
              })}

            {step === 6 &&
              Object.entries(nest).map(([pid, pl]) => {
                const p = pieces.find((x) => x.id === pid);
                const origin = boardOrigins[pl.board];
                if (!p || !origin) return null;
                const world = placePoly(p, pl, origin);
                const poly = world.map(toScreen);
                const isSel = pid === nestSel;
                const c = toScreen(
                  world.reduce((a, v) => [a[0] + v[0] / p.pts.length, a[1] + v[1] / p.pts.length], [0, 0]),
                );
                return (
                  <g key={`nest-${pid}`} data-testid={`stoneex-nest-piece-${pid}`} className="cursor-move">
                    <polygon
                      data-testid={`stoneex-nest-poly-${pid}`}
                      points={poly.map((s) => s.join(",")).join(" ")}
                      fill={isSel ? "rgba(251,191,36,0.32)" : "rgba(251,191,36,0.2)"}
                      stroke={isSel ? "#fbbf24" : "#fcd34d"}
                      strokeWidth={isSel ? 2.5 : 1.5}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        const r = svgRef.current.getBoundingClientRect();
                        setNestSel(pid);
                        setActiveBoard(pl.board);
                        setSeamMenu({ pieceId: pid, sx: e.clientX - r.left, sy: e.clientY - r.top });
                      }}
                    />
                    <text
                      x={c[0]}
                      y={c[1]}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="10"
                      fontWeight="700"
                      fill="#fde68a"
                      style={{ pointerEvents: "none" }}
                    >
                      {sqft(p.pts).toFixed(1)} SF
                    </text>
                  </g>
                );
              })}

            {step === 6 && seamDraw && seamDraw.live && (
              <polyline
                data-testid="stoneex-seam-live"
                points={[...seamDraw.path, seamDraw.live].map((s) => toScreen(s).join(",")).join(" ")}
                fill="none"
                stroke="#f43f5e"
                strokeWidth={2.5}
                strokeDasharray="6 3"
                style={{ pointerEvents: "none" }}
              />
            )}

            {step !== 6 && pieces.map((p) => {
              const scr = p.pts.map(toScreen);
              const isSel = p.id === sel;
              return (
                <g key={p.id} data-testid={`stoneex-piece-${p.id}`}>
                  <path
                    d={piecePathD(p, toScreen)}
                    fill={isSel ? "rgba(251,191,36,0.14)" : "rgba(99,102,241,0.12)"}
                    stroke={isSel ? "#fbbf24" : "#818cf8"}
                    strokeWidth={isSel ? 2 : 1.5}
                  />
                  {/* edge hit-lines + dimensions */}
                  {p.pts.map((a, i) => {
                    const b = p.pts[(i + 1) % p.pts.length];
                    const [sa, sb] = [toScreen(a), toScreen(b)];
                    const nrm = outNormal(p.pts, i);
                    const mid = edgeMid(p.pts, i);
                    const lab = toScreen([mid[0] + nrm[0] * 5, mid[1] + nrm[1] * 5]);
                    const len = edgeLen(p.pts, i);
                    if (len < 1) return null;
                    return (
                      <g key={i}>
                        <line
                          x1={sa[0]}
                          y1={sa[1]}
                          x2={sb[0]}
                          y2={sb[1]}
                          stroke="transparent"
                          strokeWidth="12"
                          style={{ cursor: tool === "pan" || tool === "draw" ? "inherit" : "move" }}
                        />
                        <g
                          data-testid={`stoneex-dim-${p.id}-${i}`}
                          className="cursor-pointer"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSel(p.id);
                            setDimEdit({
                              pieceId: p.id,
                              edge: i,
                              value: String(Math.round(len * 16) / 16),
                              sx: lab[0],
                              sy: lab[1],
                            });
                          }}
                        >
                          <rect
                            x={lab[0] - 23}
                            y={lab[1] - 9}
                            width={46}
                            height={18}
                            rx={3}
                            fill="rgba(9,9,11,0.65)"
                          />
                          <text
                            x={lab[0]}
                            y={lab[1]}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="11"
                            fontWeight="700"
                            fill={isSel ? "#fcd34d" : "#a5b4fc"}
                            style={{ pointerEvents: "none" }}
                          >
                            {fmtIn(len)}"
                          </text>
                        </g>
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {(step === 2 || step === 3) &&
              pieces.map((p) => (
                <g key={`ov-${p.id}`}>
                  {p.pts.map((v, i) => {
                    const s = toScreen(v);
                    return (
                      <circle
                        key={i}
                        data-testid={`stoneex-corner-${p.id}-${i}`}
                        cx={s[0]}
                        cy={s[1]}
                        r={5}
                        fill={(p.r || {})[i] ? "#fbbf24" : "#ffffff"}
                        stroke="#f59e0b"
                        strokeWidth="1.5"
                        className="cursor-pointer"
                        onPointerDown={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSel(p.id);
                          setCornerPopup({
                            pieceId: p.id,
                            idx: i,
                            mode: step === 3 ? "chamfer" : "round",
                            value: step === 3 ? "4" : String((p.r || {})[i] || 6),
                            sx: s[0],
                            sy: s[1],
                          });
                        }}
                      />
                    );
                  })}
                  {step === 2 &&
                    edgeSel &&
                    edgeSel.pieceId === p.id &&
                    (() => {
                      const i = edgeSel.i;
                      const mid = edgeMid(p.pts, i);
                      const nrm = outNormal(p.pts, i);
                      const dep = (p.curve || {})[i] || 0;
                      const bx = toScreen([mid[0] + nrm[0] * (dep + 3), mid[1] + nrm[1] * (dep + 3)]);
                      return (
                        <rect
                          data-testid="stoneex-curve-box"
                          x={bx[0] - 6}
                          y={bx[1] - 6}
                          width={12}
                          height={12}
                          rx={2}
                          fill="#ffffff"
                          stroke="#f59e0b"
                          strokeWidth="1.5"
                          className="cursor-grab"
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            setGesture({ type: "curve", pieceId: p.id, edge: i, mid });
                          }}
                        />
                      );
                    })()}
                </g>
              ))}

            {step === 4 &&
              pieces.map((p) =>
                (p.pts || []).map((a, i) => {
                  const meta = (p.edgeMeta || {})[i];
                  if (!meta || (!meta.finished && !meta.bs)) return null;
                  const b = p.pts[(i + 1) % p.pts.length];
                  const nrm = outNormal(p.pts, i);
                  const off = meta.bs ? 4 : 1.5;
                  const sa = toScreen([a[0] + nrm[0] * off, a[1] + nrm[1] * off]);
                  const sb = toScreen([b[0] + nrm[0] * off, b[1] + nrm[1] * off]);
                  const mid = edgeMid(p.pts, i);
                  const lab = toScreen([mid[0] + nrm[0] * 9, mid[1] + nrm[1] * 9]);
                  return (
                    <g key={`em-${p.id}-${i}`} data-testid={`stoneex-edgemeta-${p.id}-${i}`}>
                      <line
                        x1={sa[0]}
                        y1={sa[1]}
                        x2={sb[0]}
                        y2={sb[1]}
                        stroke={meta.bs ? "#38bdf8" : "#fbbf24"}
                        strokeWidth={4}
                        strokeLinecap="round"
                      />
                      {meta.bs ? (
                        <text
                          x={lab[0]}
                          y={lab[1]}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="10"
                          fontWeight="700"
                          fill="#7dd3fc"
                        >
                          {meta.bs}" BS
                        </text>
                      ) : null}
                    </g>
                  );
                }),
              )}

            {step === 5 &&
              pieces.map((p) => (
                <g key={`cut-${p.id}`}>
                  {(p.cutouts || []).map((c) => {
                    const s0 = toScreen([c.cx - c.w / 2, c.cy - c.h / 2]);
                    const s1 = toScreen([c.cx + c.w / 2, c.cy + c.h / 2]);
                    const x = Math.min(s0[0], s1[0]);
                    const y = Math.min(s0[1], s1[1]);
                    const w = Math.abs(s1[0] - s0[0]);
                    const h = Math.abs(s1[1] - s0[1]);
                    const isC = cutSel && cutSel.id === c.id;
                    const sc = toScreen([c.cx, c.cy]);
                    const common = {
                      "data-testid": `stoneex-cutout-${c.id}`,
                      className: "cursor-move",
                      fill: "rgba(244,63,94,0.18)",
                      stroke: isC ? "#f43f5e" : "#fb7185",
                      strokeWidth: isC ? 2 : 1.5,
                      onPointerDown: (e) => {
                        e.stopPropagation();
                        setSel(p.id);
                        setCutSel({ pieceId: p.id, id: c.id });
                        setGesture({ type: "cutout", pieceId: p.id, cutId: c.id });
                      },
                    };
                    return (
                      <g key={c.id}>
                        {c.shape === "round" ? (
                          <ellipse cx={sc[0]} cy={sc[1]} rx={w / 2} ry={h / 2} {...common} />
                        ) : (
                          <rect x={x} y={y} width={w} height={h} rx={3} {...common} />
                        )}
                        <text
                          x={sc[0]}
                          y={sc[1]}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="9"
                          fontWeight="700"
                          fill="#fda4af"
                          style={{ pointerEvents: "none" }}
                        >
                          {c.kind}
                        </text>
                      </g>
                    );
                  })}
                </g>
              ))}

            {/* live draw preview */}
            {drawPreview && (
              <polygon
                points={drawPreview.map((s) => toScreen(s).join(",")).join(" ")}
                fill="rgba(251,191,36,0.1)"
                stroke="#fbbf24"
                strokeWidth="1.5"
                strokeDasharray="4 3"
              />
            )}
          </svg>

          {/* empty hint */}
          {pieces.length === 0 && !draw && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <p className="rounded-xl border border-dashed border-zinc-700 px-5 py-3 text-center text-sm text-zinc-500">
                Pick <span className="text-amber-300">Draw</span>, then press and drag on the board.
                <br />
                Drag straight for a rectangle, or change direction mid-drag for an L / U shape.
              </p>
            </div>
          )}

          {cornerPopup && (
            <div
              className="absolute z-10 flex items-center gap-1 rounded-lg border border-amber-400 bg-zinc-900 p-1.5"
              style={{ left: cornerPopup.sx - 50, top: cornerPopup.sy - 46 }}
            >
              <input
                data-testid="stoneex-radius-input"
                autoFocus
                value={cornerPopup.value}
                onChange={(e) => setCornerPopup({ ...cornerPopup, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyRadius();
                  if (e.key === "Escape") setCornerPopup(null);
                }}
                placeholder="radius"
                className="w-16 rounded bg-zinc-950 px-2 py-1 text-center text-sm text-amber-200 outline-none"
              />
              <button
                data-testid="stoneex-radius-apply"
                onClick={applyRadius}
                className="rounded bg-amber-400 px-2 py-1 text-xs font-bold text-zinc-900 hover:bg-amber-300"
              >
                Apply
              </button>
            </div>
          )}

          {seamMenu && (
            <div
              data-testid="stoneex-seam-menu"
              className="absolute z-20 flex flex-col overflow-hidden rounded-lg border border-amber-400/60 bg-zinc-900 shadow-xl"
              style={{ left: Math.max(4, seamMenu.sx - 10), top: Math.max(4, seamMenu.sy - 10) }}
            >
              <button
                data-testid="stoneex-seam-menu-rotate"
                onClick={() => {
                  rotateNestSel();
                  setSeamMenu(null);
                }}
                className="px-3 py-1.5 text-left text-xs font-semibold text-zinc-200 hover:bg-zinc-800"
              >
                Rotate 90°
              </button>
              <button
                data-testid="stoneex-seam-menu-add"
                onClick={() => startSeam(seamMenu.pieceId)}
                className="px-3 py-1.5 text-left text-xs font-semibold text-amber-200 hover:bg-zinc-800"
              >
                Add seam
              </button>
              <button
                data-testid="stoneex-seam-menu-delete"
                onClick={() => deleteSeam(seamMenu.pieceId)}
                className="px-3 py-1.5 text-left text-xs font-semibold text-rose-300 hover:bg-zinc-800"
              >
                Delete seam
              </button>
            </div>
          )}

          {seamDraw && (
            <div
              data-testid="stoneex-seam-hint"
              className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full border border-rose-400/50 bg-zinc-900/90 px-4 py-1.5 text-xs font-semibold text-rose-200"
            >
              Drag across the piece — straight for a cut, bend once for a corner seam
            </div>
          )}

          {/* inline dimension editor */}
          {dimEdit && (
            <div className="absolute z-10" style={{ left: dimEdit.sx - 40, top: dimEdit.sy - 14 }}>
              <input
                data-testid="stoneex-dim-input"
                autoFocus
                value={dimEdit.value}
                onChange={(e) => setDimEdit({ ...dimEdit, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyDim();
                  if (e.key === "Escape") setDimEdit(null);
                }}
                onBlur={applyDim}
                className="w-20 rounded border border-amber-400 bg-zinc-900 px-2 py-1 text-center text-sm text-amber-200 outline-none"
              />
            </div>
          )}
        </div>

        {/* right summary panel */}
        <div className="flex w-64 shrink-0 flex-col gap-3 overflow-y-auto border-l border-zinc-800 bg-zinc-900/60 p-4">
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Drawing Summary</h3>
            <div className="mt-2 space-y-2 rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm">
              <Row label="Total Area" value={`${totalArea.toFixed(1)} SF`} testid="stoneex-summary-area" />
              <Row label="Pieces" value={String(pieces.length)} />
              <label className="flex items-center justify-between">
                <span className="text-zinc-500">Thickness</span>
                <select
                  data-testid="stoneex-thickness"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  className="rounded border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-xs text-zinc-200"
                >
                  <option>2cm</option>
                  <option>3cm</option>
                </select>
              </label>
              <Row label="Edge Profile" value={edgeProfile} />
            </div>
          </div>
          {step === 2 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm" data-testid="stoneex-curves-panel">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                Curves &amp; Radices
              </h4>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Double-click a corner dot to round it. Click a side, then drag its white box
                outward to bow it into an arc.
              </p>
              {edgeSel ? (
                <div className="mt-2 space-y-2">
                  <label className="flex items-center justify-between gap-2">
                    <span className="text-zinc-500">Curve depth (in)</span>
                    <input
                      data-testid="stoneex-curve-depth"
                      type="number"
                      value={(pieces.find((p) => p.id === edgeSel.pieceId)?.curve || {})[edgeSel.i] || 0}
                      onChange={(e) => setCurveDepth(e.target.value)}
                      className="w-20 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
                    />
                  </label>
                  <button
                    data-testid="stoneex-flatten"
                    onClick={flatten}
                    className="w-full rounded border border-zinc-700 px-2 py-1.5 text-xs font-bold text-zinc-300 hover:border-zinc-500"
                  >
                    Flatten (straight)
                  </button>
                </div>
              ) : (
                <p className="mt-2 text-xs text-zinc-600">No side selected.</p>
              )}
            </div>
          )}
          {step === 3 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm" data-testid="stoneex-bumpout-panel">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                Counter Bump-outs
              </h4>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Click a side, then add a bump-out (out) or a notch (in). Double-click a corner
                to clip it at 45&deg;.
              </p>
              {edgeSel ? (
                <div className="mt-2 space-y-2">
                  <label className="flex items-center justify-between gap-2">
                    <span className="text-zinc-500">Width (in)</span>
                    <input
                      data-testid="stoneex-bump-width"
                      type="number"
                      value={bumpW}
                      onChange={(e) => setBumpW(Math.max(2, parseFloat(e.target.value) || 0))}
                      className="w-20 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
                    />
                  </label>
                  <label className="flex items-center justify-between gap-2">
                    <span className="text-zinc-500">Depth (in)</span>
                    <input
                      data-testid="stoneex-bump-depth"
                      type="number"
                      value={bumpD}
                      onChange={(e) => setBumpD(Math.max(1, parseFloat(e.target.value) || 0))}
                      className="w-20 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      data-testid="stoneex-add-bumpout"
                      onClick={() => addBumpout(1)}
                      className="flex-1 rounded border border-zinc-700 px-2 py-1.5 text-xs font-bold text-zinc-200 hover:border-amber-400"
                    >
                      Bump-out
                    </button>
                    <button
                      data-testid="stoneex-add-notch"
                      onClick={() => addBumpout(-1)}
                      className="flex-1 rounded border border-zinc-700 px-2 py-1.5 text-xs font-bold text-zinc-200 hover:border-amber-400"
                    >
                      Notch
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-xs text-zinc-600">No side selected.</p>
              )}
            </div>
          )}
          {step === 4 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm" data-testid="stoneex-edge-panel">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                Edge &amp; Backsplash
              </h4>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Click a counter side, then mark it a finished edge or add a backsplash.
              </p>
              {edgeSel ? (
                (() => {
                  const meta =
                    (pieces.find((p) => p.id === edgeSel.pieceId)?.edgeMeta || {})[edgeSel.i] || {};
                  return (
                    <div className="mt-2 space-y-2">
                      <label className="flex items-center gap-2 text-zinc-300">
                        <input
                          data-testid="stoneex-finished-edge"
                          type="checkbox"
                          checked={!!meta.finished}
                          onChange={(e) => setEdgeMeta({ finished: e.target.checked })}
                        />
                        Finished edge
                      </label>
                      <div className="flex gap-1.5">
                        {[0, 4, 6].map((h) => (
                          <button
                            key={h}
                            data-testid={`stoneex-bs-${h}`}
                            onClick={() => setEdgeMeta({ bs: h })}
                            className={`flex-1 rounded border px-2 py-1.5 text-xs font-bold ${
                              (meta.bs || 0) === h
                                ? "border-sky-400 bg-sky-400/15 text-sky-200"
                                : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                            }`}
                          >
                            {h === 0 ? "No BS" : `${h}" BS`}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <p className="mt-2 text-xs text-zinc-600">No side selected.</p>
              )}
            </div>
          )}
          {step === 5 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm" data-testid="stoneex-cutout-panel">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                Sink &amp; Cooktop
              </h4>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Pick a cut-out to drop it on the counter, then drag it into position.
              </p>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {CUT_PRESETS.map((pr) => (
                  <button
                    key={pr.kind}
                    data-testid={`stoneex-add-cut-${pr.kind.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => addCutoutShape(pr)}
                    className="rounded border border-zinc-700 px-2 py-1.5 text-xs font-bold text-zinc-200 hover:border-rose-400"
                  >
                    {pr.kind}
                  </button>
                ))}
              </div>
              {cutSel && (
                <button
                  data-testid="stoneex-remove-cutout"
                  onClick={removeCutout}
                  className="mt-2 w-full rounded border border-rose-500/40 px-2 py-1.5 text-xs font-bold text-rose-300 hover:bg-rose-950/40"
                >
                  Remove cut-out
                </button>
              )}
            </div>
          )}
          {step === 6 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm" data-testid="stoneex-layout-panel">
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Colors &amp; Layout</h4>
              <p className="mt-1 text-xs leading-relaxed text-zinc-500">
                Pick a slab, drag pieces to match the vein, and nest them to see slab usage &amp; quote.
              </p>

              <div className="mt-2 flex flex-wrap gap-1">
                {boards.map((b, i) => (
                  <button
                    key={b.id}
                    data-testid={`stoneex-board-chip-${i}`}
                    onClick={() => setActiveBoard(i)}
                    className={`rounded px-2 py-1 text-[11px] font-bold ${
                      i === activeBoard
                        ? "bg-amber-400/20 text-amber-200 ring-1 ring-amber-400/40"
                        : "border border-zinc-700 text-zinc-300 hover:border-zinc-500"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
                <button
                  data-testid="stoneex-add-slab"
                  onClick={addBoard}
                  className="rounded border border-zinc-700 px-2 py-1 text-[11px] font-bold text-zinc-300 hover:border-amber-400"
                >
                  + Slab
                </button>
              </div>

              <div className="mt-3 space-y-2">
                <select
                  data-testid="stoneex-slab-select"
                  value={boards[activeBoard]?.slab_id || ""}
                  onChange={(e) => pickInventorySlab(e.target.value)}
                  className="w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-200"
                >
                  <option value="">Inventory slab…</option>
                  {slabLib.map((s) => (
                    <option key={s.id} value={s.id}>
                      {(s.code ? s.code + " · " : "") + s.slab_color} · {Math.round(Number(s.length_in) || 0)}"×
                      {Math.round(Number(s.width_in) || 0)}"
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    data-testid="stoneex-upload-slab"
                    onClick={() => nestPhotoRef.current?.click()}
                    className="flex-1 rounded border border-zinc-700 px-2 py-1.5 text-xs font-bold text-zinc-200 hover:border-amber-400"
                  >
                    {uploading ? "Uploading…" : "Upload slab"}
                  </button>
                  <input
                    ref={nestPhotoRef}
                    data-testid="stoneex-slab-photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadNestPhoto}
                  />
                  <button
                    data-testid="stoneex-autonest"
                    onClick={runAutoNest}
                    className="flex-1 rounded border border-zinc-700 px-2 py-1.5 text-xs font-bold text-zinc-200 hover:border-amber-400"
                  >
                    Auto-nest
                  </button>
                </div>
                <div className="flex gap-2">
                  <label className="flex flex-1 items-center justify-between gap-1 text-zinc-500">
                    L"
                    <input
                      data-testid="stoneex-slab-l"
                      type="number"
                      value={boards[activeBoard]?.L || 0}
                      onChange={(e) => setBoard(activeBoard, { L: Math.max(1, parseFloat(e.target.value) || 0) })}
                      className="w-16 rounded border border-zinc-700 bg-zinc-900 px-1.5 py-1 text-xs text-zinc-200"
                    />
                  </label>
                  <label className="flex flex-1 items-center justify-between gap-1 text-zinc-500">
                    W"
                    <input
                      data-testid="stoneex-slab-w"
                      type="number"
                      value={boards[activeBoard]?.W || 0}
                      onChange={(e) => setBoard(activeBoard, { W: Math.max(1, parseFloat(e.target.value) || 0) })}
                      className="w-16 rounded border border-zinc-700 bg-zinc-900 px-1.5 py-1 text-xs text-zinc-200"
                    />
                  </label>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <button
                  data-testid="stoneex-rotate-piece"
                  onClick={rotateNestSel}
                  disabled={!nestSel}
                  className="flex-1 rounded border border-zinc-700 px-2 py-1.5 text-xs font-bold text-zinc-200 hover:border-amber-400 disabled:opacity-40"
                >
                  Rotate 90°
                </button>
                {boards.length > 1 && (
                  <button
                    data-testid="stoneex-remove-slab"
                    onClick={removeBoard}
                    className="flex-1 rounded border border-rose-500/40 px-2 py-1.5 text-xs font-bold text-rose-300 hover:bg-rose-950/40"
                  >
                    Remove slab
                  </button>
                )}
              </div>

              <div className="mt-2 flex gap-2">
                <button
                  data-testid="stoneex-add-seam"
                  onClick={() => nestSel && startSeam(nestSel)}
                  disabled={!nestSel}
                  className="flex-1 rounded border border-amber-500/40 px-2 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-950/30 disabled:opacity-40"
                >
                  Add seam
                </button>
                {nestSel && pieces.find((x) => x.id === nestSel)?.seam && (
                  <button
                    data-testid="stoneex-delete-seam"
                    onClick={() => deleteSeam(nestSel)}
                    className="flex-1 rounded border border-rose-500/40 px-2 py-1.5 text-xs font-bold text-rose-300 hover:bg-rose-950/40"
                  >
                    Delete seam
                  </button>
                )}
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-zinc-600">
                Too big for one slab? Select a piece and <span className="text-amber-300">Add seam</span>, or
                double-click a piece, then drag the cut line across it.
              </p>

              <div className="mt-3 space-y-1 rounded border border-zinc-800 bg-zinc-900/60 p-2 text-xs">
                <Row
                  label={`${boards[activeBoard]?.label || "Slab"} used`}
                  value={`${(boardUsage[activeBoard]?.used || 0).toFixed(1)} SF`}
                  testid="stoneex-slab-used"
                />
                <Row
                  label="Offcut (waste)"
                  value={`${(boardUsage[activeBoard]?.offcut || 0).toFixed(1)} SF`}
                  testid="stoneex-slab-offcut"
                />
                <Row
                  label="Slab yield"
                  value={`${
                    boardUsage[activeBoard]?.slabSF
                      ? Math.round((boardUsage[activeBoard].used / boardUsage[activeBoard].slabSF) * 100)
                      : 0
                  }%`}
                />
              </div>

              <div className="mt-2 space-y-1 rounded border border-amber-500/30 bg-amber-950/20 p-2 text-xs">
                <h5 className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Quote estimate</h5>
                <Row label="Finished area" value={`${nestTotals.finished.toFixed(1)} SF`} testid="stoneex-q-finished" />
                <Row label="Slabs needed" value={String(nestTotals.slabs)} testid="stoneex-q-slabs" />
                {oversize.length > 0 && (
                  <p
                    data-testid="stoneex-oversize-warn"
                    className="rounded border border-rose-500/40 bg-rose-950/30 px-1.5 py-1 text-[11px] font-semibold text-rose-300"
                  >
                    {oversize.length} piece(s) exceed the slab — add a seam or a larger slab.
                  </p>
                )}
                <Row label="Real waste" value={`${nestTotals.waste.toFixed(1)} SF`} testid="stoneex-q-waste" />
                {nestTotals.rate > 0 ? (
                  <>
                    <Row
                      label="Flat 15% est."
                      value={`$${(nestTotals.finished * 1.15 * nestTotals.rate).toFixed(0)}`}
                      testid="stoneex-q-flat"
                    />
                    <Row
                      label="By slabs / real waste"
                      value={`$${(nestTotals.consumed * nestTotals.rate).toFixed(0)}`}
                      testid="stoneex-q-layout"
                    />
                  </>
                ) : (
                  <p className="text-[11px] text-zinc-500">Pick an inventory slab to price material.</p>
                )}
                {onSendToQuote && nestTotals.rate > 0 && (
                  <div className="mt-2 border-t border-amber-500/20 pt-2">
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">Bill by</div>
                    <div className="mb-2 flex gap-1.5">
                      {[["sf", "Per SF · real waste"], ["slab", "By slabs needed"]].map(([v, lbl]) => (
                        <button
                          key={v}
                          data-testid={`stoneex-bill-${v}`}
                          onClick={() => setBillMethod(v)}
                          className={`flex-1 rounded px-2 py-1 text-[11px] font-bold ${
                            billMethod === v
                              ? "bg-amber-400 text-zinc-900"
                              : "border border-zinc-700 text-zinc-300 hover:border-amber-400"
                          }`}
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                    <Row
                      label="Quote total"
                      value={`$${(nestTotals.consumed * nestTotals.rate).toFixed(0)}`}
                      testid="stoneex-q-total"
                    />
                    <button
                      data-testid="stoneex-send-to-quote"
                      onClick={sendToQuote}
                      className="mt-2 w-full rounded-lg bg-amber-400 px-3 py-2 text-xs font-bold text-zinc-900 transition-colors duration-150 hover:bg-amber-300"
                    >
                      Send to quote
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <button
            data-testid="stoneex-continue"
            onClick={() =>
              step < 6 ? setStep(step + 1) : toast("Project Details (Step 7) arrives in the next phase")
            }
            className="rounded-lg bg-amber-400 px-3 py-2.5 text-sm font-bold text-zinc-900 transition-colors duration-150 hover:bg-amber-300"
          >
            Continue to Step {Math.min(step + 1, 7)}
          </button>
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Selected Piece</h3>
            {sel ? (
              <div className="mt-2 rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm">
                {(() => {
                  const p = pieces.find((x) => x.id === sel);
                  if (!p) return <span className="text-zinc-500">—</span>;
                  return (
                    <div className="space-y-1.5">
                      <Row label="Area" value={`${sqft(p.pts).toFixed(1)} SF`} />
                      <Row label="Corners" value={String(p.pts.length)} />
                      <button
                        data-testid="stoneex-delete-piece"
                        onClick={() => {
                          commit((cur) => cur.filter((x) => x.id !== sel));
                          setSel(null);
                        }}
                        className="mt-1 flex w-full items-center justify-center gap-1 rounded border border-rose-500/40 px-2 py-1.5 text-xs font-bold text-rose-300 hover:bg-rose-950/40"
                      >
                        <X size={13} weight="bold" /> Remove piece
                      </button>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <p className="mt-2 text-xs text-zinc-600">Nothing selected. Tap a piece to edit it.</p>
            )}
          </div>
          <p className="mt-auto text-[11px] leading-relaxed text-zinc-600">
            Tip: in Select/Edit, drag a side to deepen it, or click a dimension number to type an
            exact length.
          </p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-zinc-500">{label}</span>
    <span data-testid={testid} className="font-semibold text-zinc-100">
      {value}
    </span>
  </div>
);

export default StoneExDrawing;
